/**
 * <accessories-panel> — "Complete your setup".
 *
 * Tick an accessory, set a quantity, and it joins the main product: the running
 * total appears under the price and the theme's own Add to cart button posts the
 * lot as one cart request.
 *
 * Two things worth knowing before editing:
 *
 * 1. One tab bar serves every variant row, so counts are recomputed against
 *    whichever row is visible. The Ignite variant-picker JS toggles .hidden on
 *    the [data-variant-data-variant-id] rows; we observe that rather than
 *    listening for a variant event, because the rows are the only signal the
 *    theme reliably gives us.
 *
 * 2. The Add to cart takeover is a CAPTURE listener on `document`, not on the
 *    form. Listeners on the target element itself run in registration order
 *    regardless of the capture flag, so binding to the form couldn't guarantee
 *    running before Ignite's own submit handler. It only intervenes when
 *    something is actually ticked — with nothing selected the theme's normal
 *    path runs untouched.
 */
class AccessoriesPanel extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('.ap__tab')];
    this.summary = this.querySelector('.ap__summary');
    this.selection = new Map();
    this.currency = window.Shopify?.currency?.active || 'GBP';

    this._onInput = this._handleInput.bind(this);
    this._onClick = this._handleClick.bind(this);
    this._onSubmit = this._handleSubmit.bind(this);

    this.addEventListener('change', this._onInput);
    this.addEventListener('input', this._onInput);
    this.addEventListener('click', this._onClick);
    document.addEventListener('submit', this._onSubmit, true);

    // The main quantity is part of the total, and it lives outside this element.
    this._onMainQty = (e) => {
      if (e.target?.matches?.('[name="quantity"]')) this._render();
    };
    document.addEventListener('change', this._onMainQty);
    document.addEventListener('input', this._onMainQty);

    this._observer = new MutationObserver(() => this._sync());
    this.querySelectorAll('[data-variant-data-variant-id]').forEach((el) => {
      this._observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    });

    // Don't rely on the theme un-hiding the right row. Ignite's variant JS
    // toggles .hidden on [data-variant-data-variant-id] elements, but it has
    // been seen leaving every row hidden after a size change, which empties the
    // panel. The form's own id field is the authoritative answer to "which
    // variant is selected", so poll it and drive the rows from that. Polling
    // rather than observing because the theme sets .value as a property, which
    // fires no mutation.
    this._poll = setInterval(() => this._checkVariant(), 300);

    // The pill is positioned in pixels, so it has to be re-measured when the
    // tabs reflow.
    this._onResize = () => this._positionPill();
    window.addEventListener('resize', this._onResize);

    this._checkVariant();
    this._sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this._onInput);
    this.removeEventListener('input', this._onInput);
    this.removeEventListener('click', this._onClick);
    document.removeEventListener('submit', this._onSubmit, true);
    document.removeEventListener('change', this._onMainQty);
    document.removeEventListener('input', this._onMainQty);
    this._observer?.disconnect();
    window.removeEventListener('resize', this._onResize);
    clearInterval(this._poll);
    clearTimeout(this._fadeTimer);
    clearTimeout(this._settleTimer);
    this._totalEl?.remove();
  }

  get _rows() {
    return [...this.querySelectorAll('.ap__row')];
  }

  get _visibleRow() {
    const rows = this._rows;
    return (
      rows.find((r) => r.dataset.variantDataVariantId === this._variantId) ||
      rows.find((r) => !r.classList.contains('hidden')) ||
      rows[0]
    );
  }

  _currentVariantId() {
    const field = this._form?.querySelector('[name="id"]');
    return field ? String(field.value) : null;
  }

  /** Show the row for whichever variant the form currently holds. */
  _checkVariant() {
    const id = this._currentVariantId();
    if (!id || id === this._variantId) return;
    this._variantId = id;

    const rows = this._rows;
    const match = rows.find((r) => r.dataset.variantDataVariantId === id);
    rows.forEach((r) => r.classList.toggle('hidden', match ? r !== match : r !== rows[0]));
    this._sync();
  }

  get _form() {
    return document.querySelector('product-form form[action*="/cart/add"]');
  }

  formatMoney(minor) {
    const amount = minor / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: this.currency,
      }).format(amount);
    } catch (e) {
      return '£' + amount.toFixed(2);
    }
  }

  /* ---------------- tabs ---------------- */

  /** Re-count every tab against the visible row, hide the empty ones, and make
   *  sure the active tab is one that still has something in it. */
  _sync() {
    const row = this._visibleRow;
    if (!row) return;

    if (this._lastRow && this._lastRow !== row) this._clearSelection();
    this._lastRow = row;

    let firstPopulated = null;
    let activeStillPopulated = false;
    const active = this.dataset.active;

    this.tabs.forEach((tab) => {
      const cat = tab.dataset.tab;
      const count = row.querySelectorAll(`.ap__item[data-cat="${cat}"]`).length;
      tab.querySelector('[data-count]').textContent = count;
      tab.hidden = count === 0;
      if (count > 0) {
        if (!firstPopulated) firstPopulated = cat;
        if (cat === active) activeStillPopulated = true;
      }
    });

    // Keep the customer's chosen tab across a variant change where we can; fall
    // back to the first tab that has anything rather than showing them nothing.
    this._activate(activeStillPopulated ? active : firstPopulated);

    const empty = row.querySelector('.ap__empty');
    if (empty) empty.hidden = !!firstPopulated;

    // A single remaining tab is a filter with nothing to filter.
    this.querySelector('.ap__tabs').hidden = this.tabs.filter((t) => !t.hidden).length < 2;

    this._render();
  }

  _activate(cat) {
    if (!cat) return;
    const changed = this.dataset.active !== cat;

    const markTabs = () => {
      this.tabs.forEach((tab) => {
        const on = tab.dataset.tab === cat;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this._positionPill();
    };

    const row = this._visibleRow;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!changed || !row || reduced) {
      this.dataset.active = cat;
      markTabs();
      return;
    }

    markTabs();
    this._crossfade(row, cat);
  }

  /**
   * Fade the outgoing cards away, swap, then fade the incoming ones back in
   * while the container eases between the two heights.
   *
   * The out-then-in order is the whole point: swapping display and animating
   * only the arrivals means the old cards disappear on a single frame, which
   * reads as a flicker rather than a transition. The height tween matters too —
   * tabs hold different numbers of cards, so everything below the panel jumps
   * without it.
   */
  _crossfade(row, cat) {
    clearTimeout(this._fadeTimer);
    clearTimeout(this._settleTimer);

    const fromHeight = row.getBoundingClientRect().height;
    row.classList.add('ap__row--fading');

    this._fadeTimer = setTimeout(() => {
      this.dataset.active = cat;

      // Cards are still transparent at this point, so the swap itself is unseen.
      const toHeight = row.getBoundingClientRect().height;
      row.style.overflow = 'hidden';
      row.style.height = `${fromHeight}px`;
      row.getBoundingClientRect(); // commit the start height before transitioning
      row.style.transition = 'height 280ms cubic-bezier(0.22, 0.61, 0.36, 1)';
      row.style.height = `${toHeight}px`;

      requestAnimationFrame(() => row.classList.remove('ap__row--fading'));

      this._settleTimer = setTimeout(() => {
        row.style.height = '';
        row.style.transition = '';
        row.style.overflow = '';
      }, 300);
    }, 130);
  }

  /** Slide the active pill under the current tab. */
  _positionPill() {
    const bar = this.querySelector('.ap__tabs');
    const pill = bar?.querySelector('.ap__tab-pill');
    const tab = this.tabs.find((t) => t.classList.contains('is-active') && !t.hidden);
    if (!bar || !pill || !tab) return;

    const b = bar.getBoundingClientRect();
    const t = tab.getBoundingClientRect();
    if (!t.width) return;

    // First placement shouldn't slide in from the corner.
    if (!bar.classList.contains('has-pill')) pill.style.transition = 'none';

    pill.style.width = `${t.width}px`;
    pill.style.height = `${t.height}px`;
    pill.style.transform = `translate(${t.left - b.left}px, ${t.top - b.top}px)`;

    if (!bar.classList.contains('has-pill')) {
      pill.getBoundingClientRect();
      pill.style.transition = '';
      bar.classList.add('has-pill');
    }
  }

  /* ---------------- selection ---------------- */

  _handleClick(e) {
    const tab = e.target.closest('.ap__tab');
    if (tab) {
      this._activate(tab.dataset.tab);
      return;
    }

    const step = e.target.closest('.ap__qty-step');
    if (step) {
      const input = step.parentElement.querySelector('.ap__qty-input');
      const next = (parseInt(input.value, 10) || 1) + parseInt(step.dataset.step, 10);
      input.value = Math.min(99, Math.max(1, next));
      this._readItem(step.closest('.ap__item'), true);
      this._render();
      return;
    }

    // Anywhere on the card toggles it — except the bits that already do
    // something: the title and image links, and the quantity controls. A click
    // on the checkbox itself lands here as an <input> and is left alone, since
    // the browser has already toggled it and the change event handles the rest.
    const card = e.target.closest('.ap__item');
    if (card && !e.target.closest('a, button, input, select, textarea')) {
      const check = card.querySelector('.ap__check');
      if (check && check.tagName === 'INPUT') {
        check.checked = !check.checked;
        this._readItem(card);
        this._render();
      }
      return;
    }

    const remove = e.target.closest('[data-remove]');
    if (remove) {
      const item = this._visibleRow?.querySelector(
        `.ap__item .ap__check[data-variant-id="${remove.dataset.remove}"]`
      );
      if (item) {
        item.checked = false;
        this._readItem(item.closest('.ap__item'));
      }
      this._render();
    }
  }

  _handleInput(e) {
    const el = e.target;
    if (!el.matches('.ap__check, .ap__qty-input')) return;
    // Typing a quantity is a clear enough signal that they want the thing.
    this._readItem(el.closest('.ap__item'), el.matches('.ap__qty-input'));
    this._render();
  }

  /** Push one card's current state into the selection map. */
  _readItem(item, autoTick = false) {
    if (!item) return;
    const check = item.querySelector('.ap__check');
    const qtyInput = item.querySelector('.ap__qty-input');
    if (!check || !check.dataset) return;

    if (autoTick && !check.checked) check.checked = true;

    const id = check.dataset.variantId;

    // The quantity control is inert until the card is selected — offering a
    // number to change on something you haven't chosen invites setting it and
    // wondering why nothing happened.
    item.querySelectorAll('.ap__qty-step, .ap__qty-input').forEach((el) => {
      el.disabled = !check.checked;
    });

    if (!check.checked) {
      this.selection.delete(id);
      item.classList.remove('is-selected');
      return;
    }

    const qty = Math.min(99, Math.max(1, parseInt(qtyInput?.value, 10) || 1));
    if (qtyInput) qtyInput.value = qty;
    item.classList.add('is-selected');
    this.selection.set(id, {
      id,
      qty,
      price: parseInt(check.dataset.price, 10) || 0,
      title: check.dataset.title || '',
      spec: check.dataset.spec || '',
    });
  }

  _clearSelection() {
    this.selection.clear();
    this.querySelectorAll('.ap__check').forEach((c) => {
      if (c.checked) c.checked = false;
    });
    this.querySelectorAll('.ap__qty-input').forEach((q) => {
      q.value = 1;
      q.disabled = true;
    });
    this.querySelectorAll('.ap__qty-step').forEach((b) => (b.disabled = true));
    this.querySelectorAll('.ap__item.is-selected').forEach((i) => i.classList.remove('is-selected'));
  }

  /* ---------------- rendering ---------------- */

  /** The main product's own line — always first in the summary, and not
   *  removable, since it's what the customer came for. */
  _mainLine() {
    const row = this._visibleRow;
    if (!row) return null;
    return {
      title: this.dataset.productTitle || '',
      spec: row.dataset.variantTitle === 'Default Title' ? '' : row.dataset.variantTitle || '',
      price: parseInt(row.dataset.variantPrice, 10) || 0,
      qty: parseInt(this._form?.querySelector('[name="quantity"]')?.value, 10) || 1,
    };
  }

  _render() {
    const items = [...this.selection.values()];
    const extras = items.reduce((sum, i) => sum + i.price * i.qty, 0);
    const main = this._mainLine();
    const total = (main ? main.price * main.qty : 0) + extras;

    if (this.summary) {
      const line = (i, removable) => `
        <li class="ap__summary-row${removable ? '' : ' ap__summary-row--main'}">
          <span class="ap__summary-qty">${i.qty}&times;</span>
          <span class="ap__summary-name">${i.title}${i.spec ? ` <span class="ap__summary-spec">${i.spec}</span>` : ''}</span>
          <span class="ap__summary-price">${this.formatMoney(i.price * i.qty)}</span>
          ${
            removable
              ? `<button type="button" class="ap__summary-remove" data-remove="${i.id}" aria-label="Remove ${i.title}">&times;</button>`
              : '<span class="ap__summary-remove ap__summary-remove--spacer" aria-hidden="true"></span>'
          }
        </li>`;

      this.summary.querySelector('.ap__summary-list').innerHTML = [
        main ? line(main, false) : '',
        ...items.map((i) => line(i, true)),
      ].join('');
      this.summary.querySelector('[data-order-total]').textContent = this.formatMoney(total);
    }

    this._renderTotal(extras);
    this._renderCta(total, items.length > 0);
    this._toggleDynamicCheckout(items.length > 0);
  }

  /** Say what the button will actually do: "Add setup to cart – £18.11" rather
   *  than a bare "Add to cart" that hides four other line items. */
  _renderCta(total, hasExtras) {
    const button = this._form?.querySelector('[type="submit"]');
    if (!button) return;

    const label = [...button.children].find((el) => el.tagName === 'SPAN') || button;

    // The theme re-renders this button on every variant change, so re-read the
    // original wording whenever the element itself is new — never off text we
    // may have written ourselves.
    if (this._ctaEl !== label) {
      this._ctaEl = label;
      this._ctaOriginal = label.textContent.trim();
    }

    if (!hasExtras) {
      if (this._ctaOriginal) label.textContent = this._ctaOriginal;
      return;
    }

    const wording = this.dataset.ctaLabel || 'Add setup to cart';
    label.textContent = `${wording} – ${this.formatMoney(total)}`;
  }

  /** A line under the theme's price, rather than overwriting it — the customer
   *  still needs to see what the hose itself costs, and Ignite re-renders the
   *  price element's contents on every variant change. */
  _renderTotal(extras) {
    const priceEl = document.querySelector('[id^="price-"]');
    if (!priceEl) return;

    if (!this._totalEl) {
      this._totalEl = document.createElement('p');
      this._totalEl.className = 'ap__price-total';
    }
    if (this._totalEl.parentElement !== priceEl.parentElement) {
      priceEl.insertAdjacentElement('afterend', this._totalEl);
    }

    if (!extras) {
      this._totalEl.hidden = true;
      return;
    }

    const base = parseInt(this._visibleRow?.dataset.variantPrice, 10) || 0;
    const mainQty = parseInt(this._form?.querySelector('[name="quantity"]')?.value, 10) || 1;
    const total = base * mainQty + extras;

    this._totalEl.hidden = false;
    this._totalEl.innerHTML =
      `<span class="ap__price-total-label">Total with extras</span> ` +
      `<span class="ap__price-total-value">${this.formatMoney(total)}</span>`;
  }

  /** Shop Pay and friends go straight to checkout with only the form's own
   *  line, so the extras would be silently dropped. Hide them while anything is
   *  ticked rather than letting an order lose half of what was chosen. */
  _toggleDynamicCheckout(hasExtras) {
    const dynamic = this._form?.querySelector('.shopify-payment-button');
    if (!dynamic) return;
    dynamic.hidden = hasExtras;
    let note = this._form.querySelector('.ap__checkout-note');
    if (hasExtras && !note) {
      note = document.createElement('p');
      note.className = 'ap__checkout-note';
      note.textContent = 'Use Add to cart so your extras come with it.';
      dynamic.insertAdjacentElement('afterend', note);
    } else if (note) {
      note.hidden = !hasExtras;
    }
  }

  /* ---------------- add to cart ---------------- */

  async _handleSubmit(e) {
    const form = e.target;
    if (form !== this._form || this.selection.size === 0) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    const button = form.querySelector('[type="submit"]');
    const mainId = form.querySelector('[name="id"]')?.value;
    const mainQty = parseInt(form.querySelector('[name="quantity"]')?.value, 10) || 1;
    if (!mainId) return;

    const items = [
      { id: Number(mainId), quantity: mainQty },
      ...[...this.selection.values()].map((i) => ({ id: Number(i.id), quantity: i.qty })),
    ];

    button?.setAttribute('aria-disabled', 'true');
    button?.classList.add('loading');

    try {
      const drawer = document.querySelector('cart-drawer');
      const notification = document.querySelector('cart-notification');
      const cartItems = document.querySelector('cart-items');

      let sections = [];
      if (drawer?.getSectionsToRender) sections = drawer.getSectionsToRender().map((s) => s.id);
      if (notification?.getSectionsToRender) {
        window.loadTemplateContent?.(notification);
        sections = [...sections, ...notification.getSectionsToRender().map((s) => s.id)];
      }
      if (cartItems?.getSectionsToRender) {
        sections = [...sections, ...cartItems.getSectionsToRender().map((s) => s.section)];
      }

      const action = window.theme?.settings?.cartDrawer?.atcAction;
      if (action === 'drawer' && drawer?.setActiveElement) drawer.setActiveElement(button);
      else if (action === 'notification' && notification?.setActiveElement) {
        notification.setActiveElement(button);
      }

      const res = await fetch(window.routes?.cart_add_url || '/cart/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items,
          sections: [...new Set(sections)],
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();

      if (data.status) {
        this._showError(form, data.description || data.message);
        return;
      }

      if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'accessories-panel',
          productVariantId: String(mainId),
          cartData: data,
        });
      }
      drawer?.renderContents?.(data);
      cartItems?.renderContents?.(data);
      notification?.renderContents?.(data);

      this._clearSelection();
      this._render();
    } catch (err) {
      console.error('accessories-panel: add to cart failed', err);
      this._showError(form, 'Sorry — that could not be added. Please try again.');
    } finally {
      button?.removeAttribute('aria-disabled');
      button?.classList.remove('loading');
    }
  }

  _showError(form, message) {
    const wrapper = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
    const target = wrapper?.querySelector('.product-form__error-message');
    if (!wrapper || !target) {
      alert(message);
      return;
    }
    target.textContent = message;
    wrapper.hidden = false;
  }
}

if (!customElements.get('accessories-panel')) {
  customElements.define('accessories-panel', AccessoriesPanel);
}
